// Bulk download server side downloader modal component.
// This is the 'downloading' stage of BulkDownloadModal.
// Initiates bulk download and reports progress to the user.
// We formerly had a client-side bulk downloader as well, which has been deleted.

import React, { Component } from 'react';
import { captureException } from '@sentry/browser';

import {
  getBulkDownloadServerSideZipUrl,
  BulkDownloadFilesResponse,
  BulkDownloadFile,
} from 'js/bulk_download/bulk_download_helper';
import * as arbitraryEvents from 'js/core_webpack/arbitrary_events';

const STAGES = {
  // In progress
  fetchingFileList: 'fetchingFileList',
  zipping: 'zipping',

  // End states
  success: 'success',
  error: 'error',
};

type BulkDownloadSource = 'seed-work' | 'books-search-results';

type Props = {
  // The number of files that will be downloaded
  filesTotal: number;
  // Function that closes the current modal
  closeModal: () => void;

  bundleDescription: string;
  loadBulkDownloadFiles: () => Promise<BulkDownloadFilesResponse>;
  onZipUrlSuccess?: (files: BulkDownloadFile[]) => void;
  source: BulkDownloadSource;
  extraTrackingData?: object;
};

type State = {
  stage: (typeof STAGES)[keyof typeof STAGES];
  bytesTotal: number | null;
  progress: number;
  zipUrl: string | null;
  fetchingFileListStart: number;
  zippingFilesStart: number | null;
  zipCreationComplete: number | null;
};

export default class ServerSideBulkDownloader extends Component<Props, State> {
  state: State = {
    // Current stage of the download flow
    stage: STAGES.fetchingFileList,

    // Things to show
    bytesTotal: null,
    progress: 0,

    // The final zip file AWS S3 url
    zipUrl: null,

    // Timings
    fetchingFileListStart: Math.floor(window.performance.now()),
    zippingFilesStart: null,
    zipCreationComplete: null,
  };

  linkRef: HTMLAnchorElement | null = null;

  // TODO(alex): Cancel in-progress requests instead
  _isMounted = false;
  get isMounted(): boolean {
    return this._isMounted;
  }
  set isMounted(value: boolean) {
    this._isMounted = value;
  }

  sentAnalytics = false;

  interval: number | null = null;

  componentDidMount() {
    this.isMounted = true;

    const { loadBulkDownloadFiles } = this.props;

    // Record analytics even if the user closes the window
    window.addEventListener('beforeunload', this.recordAnalyticsOnUnload);

    // The progress bar is fake because the Lambda function does not actually send progress to the client
    // For now, progress updates linearly until the Lambda is done and provides a download URL
    // If lambda is not finished by the time the bar reaches 95%, it stays there until Lambda is done
    this.interval = window.setInterval(() => this.autoupdateProgress(), 334);

    loadBulkDownloadFiles().then(this.handleGetFileListSuccess);
  }

  componentWillUnmount() {
    this.isMounted = false;

    if (this.interval) {
      clearInterval(this.interval);
    }

    // Record analytics even if the user closes the modal
    this.recordAnalytics('on modal close');
    window.removeEventListener('beforeunload', this.recordAnalyticsOnUnload);
  }

  // Linearly increases until 95% done is reached
  autoupdateProgress() {
    if (!this.isMounted) {
      return;
    }

    const { filesTotal } = this.props;
    const { progress } = this.state;
    const new_progress = progress + 95 / ((filesTotal + 1) * 4);
    if (new_progress < 95) {
      this.setState(() => ({
        progress: new_progress,
      }));
    }
  }

  handleGetFileListSuccess = (fileList: BulkDownloadFilesResponse) => {
    if (!this.isMounted) {
      return;
    }

    this.setState(
      {
        stage: STAGES.zipping,
        bytesTotal: fileList.files
          .map((file) => file.filesize)
          .reduce((a, b) => a + b, 0),
        zippingFilesStart: Math.floor(window.performance.now()),
      },
      () =>
        getBulkDownloadServerSideZipUrl(
          fileList.files,
          this.props.bundleDescription,
          fileList.zipperUrl,
        ).then(
          (zipUrl: string) => this.handleZipUrlSuccess(zipUrl, fileList.files),
          this.handleZipUrlFail,
        ),
    );
  };

  handleZipUrlSuccess = (zipUrl: string, files: BulkDownloadFile[]) => {
    if (!this.isMounted) {
      return;
    }

    if (this.interval != null) {
      clearInterval(this.interval);
    }

    if (this.props.onZipUrlSuccess) {
      this.props.onZipUrlSuccess(files);
    }

    this.setState(
      {
        stage: STAGES.success,
        zipUrl: zipUrl,
        zipCreationComplete: Math.floor(window.performance.now()),
      },
      () => this.recordAnalytics('on success'),
    );
  };

  handleZipUrlFail = (err: any) => {
    if (!this.isMounted) {
      return;
    }

    this.setState({ stage: STAGES.error }, () =>
      this.recordAnalytics('on error'),
    );
    captureException(err);
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (
      prevState.zipUrl !== this.state.zipUrl &&
      this.state.zipUrl &&
      this.linkRef
    ) {
      // Automatically start the download and close the modal
      this.linkRef.click();
      window.setTimeout(this.props.closeModal, 10000);
    }
  }

  get percentComplete() {
    const { progress } = this.state;
    switch (this.state.stage) {
      case STAGES.fetchingFileList:
        return progress;
      case STAGES.zipping:
        return progress;
      default:
        return 100;
    }
  }

  get progressText() {
    switch (this.state.stage) {
      case STAGES.fetchingFileList:
        return 'Fetching list of papers to download';
      case STAGES.zipping:
        return 'Bundling your download';
      case STAGES.success: {
        const { bundleDescription } = this.props;
        const { zipUrl } = this.state;
        return (
          <span>
            Bundling complete! Your download should start automatically, but if
            it doesn't, click this{' '}
            <a
              ref={(node) => (this.linkRef = node)}
              href={zipUrl || undefined}
              download={`Academia.edu Bundle - ${bundleDescription}.zip`}
            >
              link
            </a>
            .
            <br />
            This window will close on its own, or you can close it now.
          </span>
        );
      }
      case STAGES.error:
        return (
          <span>
            Sorry, something went wrong and we weren't able to complete your
            download. Please make sure that your connection is stable and try
            your download again. If this issue continues, please contact
            support.
          </span>
        );
      default:
        return '';
    }
  }

  render() {
    const { zipUrl, stage } = this.state;
    const progressBarColor = stage === STAGES.error ? '#e83a30' : '#0954d3';
    return (
      <div
        className="u-flexGrow1 u-displayFlex u-flexColumn u-justifyContentSpaceBetween u-alignItemsCenter"
        style={{ height: '100%' }}
      >
        <div
          className="u-taCenter u-pv4x"
          style={{
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <div className="u-fw700 u-fs20 u-mb1x" style={{ lineHeight: '28px' }}>
            {!zipUrl ? 'Preparing your download' : 'Your download is ready'}
          </div>

          <div
            className="u-fontStyleItalic u-fs18 u-mb8x"
            style={{ lineHeight: '21px', opacity: !zipUrl ? 1 : 0 }}
          >
            Please don't close this window. It will close automatically when
            your download begins.
          </div>

          <div className="u-fw500 u-fs14 u-mb3x" style={{ lineHeight: '20px' }}>
            {this.progressText}
          </div>

          <div
            style={{
              width: '50%',
              minWidth: 200,
              height: 20,
              border: `1px solid ${progressBarColor}`,
              borderRadius: 4,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <div
              style={{
                width: this.percentComplete + '%',
                height: 18,
                backgroundColor: progressBarColor,
              }}
            />
          </div>
        </div>
      </div>
    );
  }
  recordAnalyticsOnUnload = () => this.recordAnalytics('on window unload');
  recordAnalytics(whenSent: string) {
    // Record analytics once per bulk download attempt
    if (this.sentAnalytics) {
      return;
    }
    this.sentAnalytics = true;

    const { filesTotal, source, extraTrackingData } = this.props;
    const {
      stage,
      bytesTotal,
      fetchingFileListStart,
      zippingFilesStart,
      zipCreationComplete,
    } = this.state;

    let data = {
      stage_reached: stage,
      when_sent: whenSent,

      source: source,

      bytes_total: bytesTotal,
      files_total: filesTotal,

      fetching_file_list_start: fetchingFileListStart,
      zipping_start: zippingFilesStart,
      zip_creation_complete: zipCreationComplete,
    };

    data = Object.assign(data, extraTrackingData);

    arbitraryEvents.write('BulkDownloadServerSide', data);
  }
}
